import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CurecallInputLabel, CurecallToggle } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useId, useState } from 'react'
import { TChatbotGeneralFormValues } from '../chatbot-form.type'
import { formatCombinedIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'
import {
  DurationInputFields,
  type TDelayState,
} from 'src/components/duration-input-fields/duration-input-fields.component'

const DEFAULT_DELAY: TDelayState = { days: 0, hours: 1, minutes: 0 }

export function ChatbotShowUnreachedField() {
  const { t } = useTranslation()
  const id = useId()

  const {
    formState: { errors },
    control,
    setValue,
    clearErrors,
  } = useFormContext<TChatbotGeneralFormValues>()

  const incompleteQuestionnaireDelay = useWatch({
    control,
    name: 'options.requestHumanReview.incompleteQuestionnaireDelay',
  })

  const [delay, setDelay] = useState<TDelayState>(() => {
    if (incompleteQuestionnaireDelay) {
      const parsed = parseCombinedIsoDuration(incompleteQuestionnaireDelay)
      return { days: parsed.days, hours: parsed.hours, minutes: parsed.minutes }
    }
    return DEFAULT_DELAY
  })

  const updateDelay = (newDelay: TDelayState) => {
    setDelay(newDelay)
    setValue('options.requestHumanReview.incompleteQuestionnaireDelay', formatCombinedIsoDuration(newDelay))
    clearErrors('options.requestHumanReview.incompleteQuestionnaireDelay')
  }

  return (
    <div className="c-flex c-flex-col c-gap-4">
      <Controller
        control={control}
        name="options.requestHumanReview.incompleteQuestionnaireDelay"
        render={({ field }) => (
          <CurecallToggle
            leftLabel={t(
              'pages.configuration.followups.chatbot.general-form.show-unreached-patient-in-dashboard',
            )}
            isEnabled={!!field.value}
            onChange={(enabled) => {
              if (enabled) {
                setDelay(DEFAULT_DELAY)
                setValue(
                  'options.requestHumanReview.incompleteQuestionnaireDelay',
                  formatCombinedIsoDuration(DEFAULT_DELAY),
                )
              } else {
                setValue('options.requestHumanReview.incompleteQuestionnaireDelay', null)
                clearErrors('options.requestHumanReview.incompleteQuestionnaireDelay')
                setDelay(DEFAULT_DELAY)
              }
            }}
            tooltipContent={t(
              'pages.configuration.followups.chatbot.general-form.show-unreached-patient-in-dashboard-tooltip',
            )}
            tooltipPosition="right"
          />
        )}
      />

      {incompleteQuestionnaireDelay && (
        <div className="c-flex c-flex-col c-pl-6 c-border-l-2 c-border-gray-200 c-mb-4">
          <CurecallInputLabel
            id={id}
            label={t('pages.configuration.followups.chatbot.general-form.show-unreached-delay')}
            isRequired={true}
            tooltipContent={t(
              'pages.configuration.followups.chatbot.general-form.show-unreached-delay-tooltip',
            )}
            tooltipPosition="right"
          />
          <Controller
            control={control}
            name="options.requestHumanReview.incompleteQuestionnaireDelay"
            rules={{
              validate: (value) => {
                if (!value) return true
                const { days, hours, minutes } = parseCombinedIsoDuration(value)
                return days * 24 * 60 + hours * 60 + minutes >= 60
              },
            }}
            render={() => (
              <div className="c-flex c-flex-col c-gap-2">
                <DurationInputFields delay={delay} onDelayChange={updateDelay} />
                {errors.options?.requestHumanReview?.incompleteQuestionnaireDelay && (
                  <span className="c-text-sm c-text-danger-600">
                    {t('pages.configuration.followups.chatbot.general-form.show-unreached-delay-error')}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  )
}
